import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Grid, Typography, Tooltip } from "@mui/material";
import * as Yup from "yup";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import FormBuilder from "../../../../../../shared/components/form-builder/form-builder";
import ModalPopup from "../../../../../../shared/components/modals/custom-popup";
import { ActionsMenu } from "../../../../../../shared/components/table/custom-actions";
import CustomTable from "../../../../../../shared/components/table/custom-table";
import { MRT_Cell, MRT_ColumnDef } from "material-react-table";
import {
  FloatingButtonContainer,
  FloatingContainer,
  InnerButton,
} from "../../../../../../shared/components/buttons/floating-button-container";
import { dtrackItService } from "../../../tc-service/dtrackit.service";
import { transactionCertificateService } from "../../../tc-service/transaction-certificate.service";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { IShipmentsRequest } from "../../../tc-models/tc-req-dto/tc-shipments.req_dto";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { shipmentsDetailsService } from "../../../tc-service/shipments.service";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { buyerSellerDetailsService } from "../../../tc-service/buyer-seller-details.service";
import {
  DELETECONFIG,
  TC_CREATE_ROUTE_CONFIG,
  TCAppConfig,
} from "../../../../../../shared/app-configs/tc-app-configs";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { LoaderComponent } from "../../../../../../shared/components/loaders/page-loader";
import AlertPopup from "../../../../../../shared/components/modals/alert-moadal";
import { useTransactionCertificate } from "../../../../../../shared/hooks/transaction-certificate.hooks";
import _ from "lodash";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { RestrictedDataValidation } from "../../../../../../shared/utils/custom-validation.utils";
import RenderDetailPanel from "./render-detail-panel";
import { ApiShipmentResponseItem } from "./shipments.model";
import { createPayload, fetchShipmentDetails } from "./shipment-api";

// Types and Interfaces
export interface IShipmentDetail {
  shipmentId: string | null;
  tcShipmentNo: string;
  tcShipmentDate: string;
  tcShipmentDocNo: string;
  tcShipmentGrossShippingWeight: string;
  tcShipmentInvoiceReferences: string[];
  tcShipToName: string;
  tcShipToTeId: string;
  tcShipToStreetAddress: string;
  tcShipToPostcode: string;
  tcShipToTown: string;
  tcShipToStateOrProvince: string;
  tcShipToCountryOrArea: string;
  tcShipToCountryOrAreaDescription?: string;
  tcShipToStateOrProvinceDescription?: string;
  tcShipToArrangedByBuyer: false;
}

interface ShipmentsProps {
  onNextTab: () => Promise<void>;
}

interface ApiResponse {
  data: {
    response: ApiShipmentResponseItem[];
  };
}

interface TEDetail {
  TE_ID: string;
  account_name: string;
  street?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  state?: string;
}

// Constants
const MAX_SHIPMENTS_ALLOWED = 50;
const DISALLOWED_SHIP_TO_NAME_VALUES = new Set(["n/a", "not available", "null"]);

const normalizeNaValue = (value: string) =>
  value.trim().toLowerCase() === "na" ? "na" : value.trim();

const isValidShipToName = (value?: string | null) => {
  const normalized = String(value || "").trim().toLowerCase();
  return Boolean(normalized) && !DISALLOWED_SHIP_TO_NAME_VALUES.has(normalized);
};

export const shipmentDefaults: IShipmentDetail = {
  shipmentId: null,
  tcShipmentNo: "",
  tcShipmentDate: "",
  tcShipmentDocNo: "",
  tcShipmentGrossShippingWeight: "",
  tcShipmentInvoiceReferences: [],
  tcShipToName: "",
  tcShipToTeId: "",
  tcShipToStreetAddress: "",
  tcShipToPostcode: "",
  tcShipToTown: "",
  tcShipToStateOrProvince: "",
  tcShipToCountryOrArea: "",
  tcShipToCountryOrAreaDescription: "",
  tcShipToStateOrProvinceDescription: "",
  tcShipToArrangedByBuyer: false,
};

const Shipments: React.FC<ShipmentsProps> = ({ onNextTab }) => {
  const { TE_ID, formtype } = useParams();
  const hasCreateTc: boolean = formtype === "create-tc";
  const hasSubmitTc: boolean = formtype === "submited-tc";
  const hasRejectedTc: boolean = formtype === "rejected-tc";

  // Form Related State
  const formRef = useRef<any>(null);
  const dateWarningResolverRef = useRef<((value: boolean) => void) | null>(null);
  const [initialValues, setInitialValues] = useState<IShipmentDetail>({
    ...shipmentDefaults,
    tcShipmentDate: shipmentDefaults.tcShipmentDate || "",
    tcShipmentDocNo: shipmentDefaults.tcShipmentDocNo || "",
    tcShipmentGrossShippingWeight:
      shipmentDefaults.tcShipmentGrossShippingWeight || "0",
    tcShipmentInvoiceReferences:
      shipmentDefaults.tcShipmentInvoiceReferences || [],
    tcShipToName: shipmentDefaults.tcShipToName || "",
    tcShipToTeId: shipmentDefaults.tcShipToTeId || "",
    tcShipToStreetAddress: shipmentDefaults.tcShipToStreetAddress || "",
    tcShipToPostcode: shipmentDefaults.tcShipToPostcode || "",
    tcShipToTown: shipmentDefaults.tcShipToTown || "",
    tcShipToCountryOrArea: shipmentDefaults.tcShipToCountryOrArea || "",
    tcShipToStateOrProvince: shipmentDefaults.tcShipToStateOrProvince || "",
    tcShipToCountryOrAreaDescription:
      shipmentDefaults.tcShipToCountryOrAreaDescription || "",
    tcShipToStateOrProvinceDescription:
      shipmentDefaults.tcShipToStateOrProvinceDescription || "",
    tcShipToArrangedByBuyer: shipmentDefaults.tcShipToArrangedByBuyer || false,
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [countries, setCountries] = useState<
    { value: string; label: string }[]
  >([]);
  const [states, setState] = useState<{ value: string; label: string }[]>([]);
  const [disable, setDisable] = useState({
    disableShipToCountryOrArea: true,
    disableShipToStateOrProvince: true,
    disableShipToPostCode: true,
    disableShipToTown: true,
    disableShipToAddress1: true,
  });
  const [isTEEnabled, setTEEnabled] = useState<boolean>(true);
  const [selectedShipmentId, setSelectedShipmentId] = useState<string | null>(
    null
  );
  const [getCopy, setCopy] = useState(false);
  const [pendingSave, setPendingSave] = useState<IShipmentDetail | null>(null);

  // Table Related State
  const [tableData, setTableData] = useState<IShipmentDetail[]>([]);
  const [data, setData] = useState<IShipmentDetail[]>([]);
  const [apiShipmentsData, setAPIShipmentsData] = useState<IShipmentDetail[]>(
    []
  );
  const [apiData, setAPIData] = useState<IShipmentDetail[]>([]);
  const [getEditRowIndex, setEditRowIndex] = useState(-1);
  const [getEditExistingShipment, setEditExistingShipment] = useState({
    index: -1,
    value: "",
  });
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [shipmentMinDate, setShipmentMinDate] = useState<string | null>(null);

  // Dialog and Notification State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [originalValue, setOriginalValue] = useState("");
  const [popUpMessage, setPopUpMessage] = useState<string>("");
  const [popUpTitle, setPopUpTitle] = useState<string>("");
  const [popUpBtnName, setPopUpBtnName] = useState<string>("");
  const [tcShipmentNo, setTcShipmentNo] = useState<string | null>(null);
  const [dateWarningDialog, setDateWarningDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
  }>({
    open: false,
    title: "",
    message: "",
  });

  // General State
  const [isNextEnabled, setNextEnabled] = useState<boolean>(false);
  const [isDeleteOnly, setDeleteOnly] = useState(false);

  // Hooks
  const { notify } = useNotification();
  const navigate = useNavigate();

  const {
    loading,
    error,
    executeGet,
    setLoadingState,
    executePost,
    executeDelete,
  } = useApi();

  const { loading: loadingAdditional, executeGet: executeAdditionalGet } =
    useApi();

  const {
    isDelayResonSet,
    tcIssueDate,
    setStates,
    setCountryCodes,
    getTCStandardGOTS,
  } = useTransactionCertificateStore();

  const {
    getCurrentFormValues,
    getCurrentTableData,
    updateTableData,
    updateField,
    updateTableRowbyIndex,
    removeTableRowbyIndex,
    setNewDataTCSaved,
    setEditSave,
    setDeleteSave,
    resetFormValueByPage,
    getDeleteSave,
    replaceTableDatas,
  } = useFormActions("shipments");

  // FORM LOGIC SECTION

  // Form Validation Schema
  const shipmentDetailValidationSchema = Yup.object().shape({
    tcShipmentDate: Yup.date()
      .required("Shipment Date is required")
      .test(
        "shipment-date-before-issue-date",
        "Shipment date must be on or before the TC Issue Date in Transaction Details Tab",
        function (shipmentDate) {
          if (!tcIssueDate || !shipmentDate) return true;
          return new Date(shipmentDate) <= new Date(tcIssueDate);
        }
      )
      .test(
        "is-within-180-days",
        "Shipment date cannot be more than 180 days from today",
        function (value: any) {
          if (!isDelayResonSet && tcIssueDate) {
            const differenceInDays = Math.abs(
              Utills.calculateDateDifferenceInDays(tcIssueDate, value)
            );
            // Debug: Difference in days removed
            if (differenceInDays >= 180) {
              return this.createError({
                message:
                  "Please Fill Delay Reason in Transaction Details Tab, if Shipment date is more than 180 days before issue date",
              });
            }
          }
          return true;
        }
      ),

    tcShipmentDocNo: Yup.string()
      .matches(/^[^\s]+$/, "Shipment Doc Number cannot have white spaces")
      .required("Shipment Document Number is required")
      .max(40, "Maximum 40 Character is Allowed"),

    tcShipmentGrossShippingWeight: Yup.string()
      .required("Shipment Gross Shipping Weight is required")
      .test(
        "is-max-five-decimal-places",
        "Number must be a valid decimal number with up to 5 decimal places.",
        (value: string | undefined) => {
          if (!value) return true;
          return /^(0|[0-9]\d*|0\.\d{1,5}|[0-9]\d*\.\d{1,5})$/.test(value);
        }
      )
      .typeError("Shipment Gross Shipping Weight must be a valid number"),

    tcShipmentInvoiceReferences: Yup.array()
      .min(1, "At least one invoice reference is required")
      .required("TC Shipment Invoice References is required")
      .test(
        "all-valid-invoice-refs",
        "Invoice Reference Number cannot have white spaces and can have upto 255 characters",
        (arr) => {
          if (!arr || arr.length === 0) return false;
          return arr.every(
            (val) =>
              typeof val === "string" &&
              /^[^\s]+$/.test(val) &&
              val.length <= 555
          );
        }
      ),
    tcShipToName: Yup.string()
      .required("Name is required")
      .test(
        "valid-ship-to-name",
        "Ship To Name cannot be blank, N/A, Not available, or null",
        (value) => isValidShipToName(value)
      ),
    tcShipToStreetAddress: Yup.string()
      .when("tcShipToName", {
        is: (tcShipToName: string) => {
          if (!tcShipToName) return true;
          const normalized = String(tcShipToName).toLowerCase().trim();
          return normalized !== "arranged by buyer" && normalized !== "arranged by the buyer";
        },
        then: Yup.string()
          .required("Address is required")
          .test(
            "not-restricted-data",
            "Address cannot be NA, N/A, Not available, or n/a",
            (value) => !RestrictedDataValidation.isRestrictedData(value)
          ),
        otherwise: Yup.string().nullable(),
      }),
    tcShipToTown: Yup.string()
      .when("tcShipToName", {
        is: (tcShipToName: string) => {
          if (!tcShipToName) return true;
          const normalized = String(tcShipToName).toLowerCase().trim();
          return normalized !== "arranged by buyer" && normalized !== "arranged by the buyer";
        },
        then: Yup.string()
          .required("Town is required")
          .test(
            "not-restricted-data",
            "Town cannot be NA, N/A, Not available, or n/a",
            (value) => !RestrictedDataValidation.isRestrictedData(value)
          ),
        otherwise: Yup.string().nullable(),
      }),
    tcShipToStateOrProvince: Yup.string()
      .when("tcShipToName", {
        is: (tcShipToName: string) => {
          if (!tcShipToName) return true;
          const normalized = String(tcShipToName).toLowerCase().trim();
          return normalized !== "arranged by buyer" && normalized !== "arranged by the buyer";
        },
        then: Yup.string()
          .required("State is required")
          .test(
            "not-restricted-data",
            "State cannot be NA, N/A, Not available, or n/a",
            (value) => !RestrictedDataValidation.isRestrictedData(value)
          ),
        otherwise: Yup.string().nullable(),
      }),
    tcShipToCountryOrArea: Yup.string()
      .when("tcShipToName", {
        is: (tcShipToName: string) => {
          if (!tcShipToName) return true;
          const normalized = String(tcShipToName).toLowerCase().trim();
          return normalized !== "arranged by buyer" && normalized !== "arranged by the buyer";
        },
        then: Yup.string()
          .required("Country is required")
          .test(
            "not-restricted-data",
            "Country cannot be NA, N/A, Not available, or n/a",
            (value) => !RestrictedDataValidation.isRestrictedData(value)
          ),
        otherwise: Yup.string().nullable(),
      }),
    tcShipmentConsigneeOrder: Yup.string().nullable(),
    tcShipToTeId: Yup.string()
      .required("TE id is required")
      .matches(
        /^TE-\d{6,10}$/,
        "Invalid TE-ID. Expected format TE-###### to TE-########## (6-10 digits)"
      ),
    shipToAddress2: Yup.string().nullable(),
    shipToAddress3: Yup.string().nullable(),
    tcShipToPostcode: Yup.string()
      .when("tcShipToName", {
        is: (tcShipToName: string) => {
          if (!tcShipToName) return true;
          const normalized = String(tcShipToName).toLowerCase().trim();
          return normalized !== "arranged by buyer" && normalized !== "arranged by the buyer";
        },
        then: Yup.string()
          .required("Postcode is required")
          .test(
            "not-restricted-data",
            "Postcode cannot be NA, N/A, Not available, or n/a",
            (value) => !RestrictedDataValidation.isRestrictedData(value)
          ),
        otherwise: Yup.string().nullable(),
      }),
    ocsFirstProcessor: Yup.string().nullable(),
  });

  // Form Handlers
  const resetForm = () => {
    setInitialValues({ ...shipmentDefaults });
  };

  const toggleDrawer = () => {
    setZustandInitialState();
    if (drawerOpen) {
      resetForm();
      setSelectedShipmentId(null);
      setPendingSave(null);
    } else {
      // Check shipment limit before opening drawer for new shipment
      if (
        !selectedShipmentId &&
        !getCopy &&
        tableData.length >= MAX_SHIPMENTS_ALLOWED
      ) {
        notify(
          "Maximum limit of 50 shipments reached. Please remove some shipments before adding new ones.",
          "error"
        );
        return;
      }
    }
    setDrawerOpen((prev) => !prev);
  };

  const handleSubmit = async () => {
    if (formRef.current) {
      try {
        console.debug("Selected Shipment ID:", selectedShipmentId);
        await formRef.current.submitForm();
        // Debug: Form submitted successfully
      } catch (error) {
        console.error("Error during form submission:", error);
        notify("Error during form submission", "error");
      }
    } else {
      console.error("formRef is null");
      notify("Form reference is not available", "error");
    }
  };

  const handleArrangedByField = async (value: any) => {
    formRef.current.setFieldValue(
      "tcShipToArrangedByBuyer",
      value?.currentTarget?.checked
    );
    if (value?.currentTarget?.checked === true) {
      formRef.current.setFieldValue("tcShipToName", "Arranged By Buyer");
      formRef.current.setFieldValue("tcShipToStreetAddress", "na");
      formRef.current.setFieldValue("tcShipToTown", "na");
      formRef.current.setFieldValue("tcShipToPostcode", "na");
      formRef.current.setFieldValue("tcShipToCountryOrArea", "00");
      await getState("00");
      formRef.current.setFieldValue("tcShipToStateOrProvince", "00");
    } else {
      formRef.current.setFieldValue("tcShipToName", "");
      formRef.current.setFieldValue("tcShipToStreetAddress", "");
      formRef.current.setFieldValue("tcShipToTown", "");
      formRef.current.setFieldValue("tcShipToPostcode", "");
      formRef.current.setFieldValue("tcShipToStateOrProvince", "");
      formRef.current.setFieldValue("tcShipToCountryOrArea", "");
    }
  };
  // TE Detail Handlers
  // Updated handleShipToTEIdValueChange function
  const handleShipToTEIdValueChange = async (value: any) => {
    const fields = [
      "tcShipToName",
      "tcShipToStreetAddress",
      "tcShipToTown",
      "tcShipToPostcode",
      "tcShipToCountryOrArea",
      "tcShipToStateOrProvince",
      "tcShipToTeId",
    ];

    if (formRef?.current?.values?.tcShipToArrangedByBuyer === true) {
      formRef.current.setFieldValue("tcShipToTeId", value?.currentTarget.value);
    } else if (value == null) {
      fields.forEach((field) => {
        if (formRef.current) {
          formRef.current.setFieldValue(field, "");
        }
      });
      setTEEnabled(true);
      setDisable((prev) => ({
        ...prev,
        disableShipToCountryOrArea: false,
        disableShipToStateOrProvince: false,
        disableShipToPostCode: false,
        disableShipToTown: false,
        disableShipToAddress1: false,
      }));
    } else {
      if (formRef.current) {
        formRef.current.setFieldValue("tcShipToTeId", value.TE_ID);
        formRef.current.setFieldValue("tcShipToName", value.account_name);
        setTEEnabled(false);

        // Address handling
        if (!value.street) {
          setDisable((prev) => ({ ...prev, disableShipToAddress1: false }));
          formRef.current.setFieldValue("tcShipToStreetAddress", "");
        } else {
          setDisable((prev) => ({ ...prev, disableShipToAddress1: true }));
          formRef.current.setFieldValue(
            "tcShipToStreetAddress",
            value.street || ""
          );
        }

        // City handling
        if (!value.city) {
          setDisable((prev) => ({ ...prev, disableShipToTown: false }));
        } else {
          setDisable((prev) => ({ ...prev, disableShipToTown: true }));
          formRef.current.setFieldValue("tcShipToTown", value.city || "");
        }

        // Country handling and state loading
        if (value.country) {
          try {
            await getState(value.country);
          } catch (error) {
            console.error("Error loading states:", error);
            notify("Failed to load states for selected country", "error");
          }
        }

        // Postal code handling
        if (!value.postal_code) {
          setDisable((prev) => ({ ...prev, disableShipToPostCode: false }));
        } else {
          setDisable((prev) => ({ ...prev, disableShipToPostCode: true }));
          formRef.current.setFieldValue("tcShipToPostcode", value.postal_code);
        }

        // Country field handling
        if (!value.country) {
          setDisable((prev) => ({
            ...prev,
            disableShipToCountryOrArea: false,
          }));
        } else {
          setDisable((prev) => ({ ...prev, disableShipToCountryOrArea: true }));
          formRef.current.setFieldValue("tcShipToCountryOrArea", value.country);
        }

        // State field handling
        if (!value.state) {
          setDisable((prev) => ({
            ...prev,
            disableShipToStateOrProvince: false,
          }));
          formRef.current.setFieldValue("tcShipToStateOrProvince", "");
        } else {
          setDisable((prev) => ({
            ...prev,
            disableShipToStateOrProvince: true,
          }));
          formRef.current.setFieldValue("tcShipToStateOrProvince", value.state);
        }
      }
    }
  };

  // Updated handleShipToNameValueChange function
  const handleShipToNameValueChange = async (value: any) => {
    const fields = [
      "tcShipToName",
      "tcShipToStreetAddress",
      "tcShipToTown",
      "tcShipToPostcode",
      "tcShipToCountryOrArea",
      "tcShipToStateOrProvince",
      "tcShipToTeId",
    ];

    if (formRef?.current?.values?.tcShipToArrangedByBuyer === true) {
      formRef.current.setFieldValue("tcShipToName", value?.currentTarget.value);
    } else if (value == null) {
      fields.forEach((field) => {
        if (formRef.current) {
          formRef.current.setFieldValue(field, "");
        }
      });
      setTEEnabled(true);
      setDisable((prev) => ({
        ...prev,
        disableShipToCountryOrArea: false,
        disableShipToStateOrProvince: false,
        disableShipToPostCode: false,
        disableShipToTown: false,
        disableShipToAddress1: false,
      }));
    } else {
      if (formRef.current) {
        formRef.current.setFieldValue("tcShipToTeId", value.TE_ID);
        formRef.current.setFieldValue("tcShipToName", value.account_name);
        setTEEnabled(false);

        // Address handling
        if (!value.street) {
          setDisable((prev) => ({ ...prev, disableShipToAddress1: false }));
          formRef.current.setFieldValue("tcShipToStreetAddress", "");
        } else {
          setDisable((prev) => ({ ...prev, disableShipToAddress1: true }));
          formRef.current.setFieldValue(
            "tcShipToStreetAddress",
            value.street || ""
          );
        }

        // City handling
        if (!value.city) {
          setDisable((prev) => ({ ...prev, disableShipToTown: false }));
        } else {
          setDisable((prev) => ({ ...prev, disableShipToTown: true }));
          formRef.current.setFieldValue("tcShipToTown", value.city || "");
        }

        // Country handling and state loading
        if (
          value.country &&
          value.country !== "" &&
          value.country !== undefined
        ) {
          try {
            await getState(value.country);
          } catch (error) {
            console.error("Error loading states:", error);
            notify("Failed to load states for selected country", "error");
          }
        }

        // Postal code handling
        if (!value.postal_code) {
          setDisable((prev) => ({ ...prev, disableShipToPostCode: false }));
        } else {
          setDisable((prev) => ({ ...prev, disableShipToPostCode: true }));
          formRef.current.setFieldValue("tcShipToPostcode", value.postal_code);
        }

        // Country field handling
        if (!value.country) {
          setDisable((prev) => ({
            ...prev,
            disableShipToCountryOrArea: false,
          }));
        } else {
          setDisable((prev) => ({ ...prev, disableShipToCountryOrArea: true }));
          formRef.current.setFieldValue("tcShipToCountryOrArea", value.country);
        }

        // State field handling
        if (!value.state) {
          setDisable((prev) => ({
            ...prev,
            disableShipToStateOrProvince: false,
          }));
          formRef.current.setFieldValue("tcShipToStateOrProvince", "");
        } else {
          setDisable((prev) => ({
            ...prev,
            disableShipToStateOrProvince: true,
          }));
          formRef.current.setFieldValue("tcShipToStateOrProvince", value.state);
        }
      }
    }
  };

  // Form Configuration
  const formConfig = {
    pageName: "shipments",
    initialValues: initialValues,
    validationSchema: shipmentDetailValidationSchema,
    sections: [
      {
        title: "Shipment Details",
        fields: [
          // Row 1: Column 1 - Doc No, Column 2 - Date, Column 3 - Gross Weight
          {
            label: "TC Shipment Doc Number",
            name: "tcShipmentDocNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: initialValues.tcShipmentDocNo,
            readOnly: hasSubmitTc,
            props: {
              placeholder: "Enter the Shipment Doc Number",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "TC Shipment Date",
            name: "tcShipmentDate",
            type: "date",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            events: {
              onChange: (value: any) => {
                // Debug: shipment date changed
              },
            },
            value: initialValues.tcShipmentDate,
            props: {
              placeholder: "YYYY-MM-DD",
              fullWidth: true,
            },
          },
          {
            label: "TC Shipment Gross Shipping Weight",
            name: "tcShipmentGrossShippingWeight",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: initialValues.tcShipmentGrossShippingWeight,
            props: {
              placeholder: "Enter the Gross Shipping Weight",
              variant: "outlined",
              fullWidth: true,
              type: "number",
            },
          },
          // Row 2: Full Width - Invoice Reference
          {
            label: "TC Shipment Invoice Reference Number",
            name: "tcShipmentInvoiceReferences",
            type: "chips",
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: true,
            value: initialValues.tcShipmentInvoiceReferences || [],
            props: {
              placeholder: "Enter the Shipment Invoice Reference Numbers (Press Enter on keyboard after each value)",
              fullWidth: true,
            },
          },
        ],
      },
      {
        title: "Shipment To Details",
        fields: [
          {
            label: "Arranged By Buyer",
            name: "tcShipToArrangedByBuyer",
            type: "checkbox",
            gridSize: { xs: 12, md: 4 },
            required: false,
            value: initialValues.tcShipToArrangedByBuyer,
            props: {
              pageName: "shipments",
              variant: "outlined",
              onChange: handleArrangedByField,
            },
          },
          // Row 1: Column 1 - TE-ID, Column 2 - Name
          {
            label: "Ship To TE-ID",
            name: "tcShipToTeId",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: initialValues.tcShipToTeId,
            visible: () => formRef?.current?.values?.tcShipToArrangedByBuyer,
            props: {
              pageName: "shipments",
              placeholder: "Enter Buyer TE ID",
              variant: "outlined",
              restrictInputFormat: isTEEnabled,
              onChange: handleShipToTEIdValueChange,
              displayKey: "TE_ID",
              fullWidth: true,
            },
          },
          {
            label: "Ship To TE-ID",
            name: "tcShipToTeId",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: initialValues.tcShipToTeId,
            visible: () => !formRef?.current?.values?.tcShipToArrangedByBuyer,
            props: {
              pageName: "shipments",
              placeholder: "TE- Search for the TE-ID",
              variant: "outlined",
              restrictInputFormat: isTEEnabled,
              onChange: handleShipToTEIdValueChange,
              displayKey: "TE_ID",
              onSearch: (query: string, offset: number) => {
                return fetchTEDetallsFromAPI(query, offset, "query");
              },
              fullWidth: true,
            },
          },
          {
            label: "Ship To Name",
            name: "tcShipToName",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: initialValues.tcShipToName,
            visible: () => formRef?.current?.values?.tcShipToArrangedByBuyer,
            props: {
              pageName: "shipments",
              placeholder: "Search for the Name",
              variant: "outlined",
              onChange: handleShipToNameValueChange,
              displayKey: "account_name",
              fullWidth: true,
            },
          },
          {
            label: "Ship To Name",
            name: "tcShipToName",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: initialValues.tcShipToName,
            visible: () => !formRef?.current?.values?.tcShipToArrangedByBuyer,
            props: {
              pageName: "shipments",
              placeholder: "Search for the Name",
              variant: "outlined",
              onChange: handleShipToNameValueChange,
              displayKey: "account_name",
              onSearch: (query: string, offset: number) => {
                return fetchTEDetallsFromAPI(query, offset, "name");
              },
              fullWidth: true,
            },
          },
          // Row 2: Full Width - Street Address
          {
            label: "Ship To Street Address",
            name: "tcShipToStreetAddress",
            type: "text",
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: true,
            value: initialValues.tcShipToStreetAddress,
            props: {
              placeholder: "Enter the Address",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Row 3: Column 1 - Town, Column 2 - Postcode
          {
            label: "Ship To Town",
            name: "tcShipToTown",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: initialValues.tcShipToTown,
            props: {
              placeholder: "Enter the Town (Optional)",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Ship To Postcode",
            name: "tcShipToPostcode",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: initialValues.tcShipToPostcode,
            props: {
              placeholder: "Enter the Postcode (Optional)",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Row 4: Column 1 - Country/Area, Column 2 - State/Province
          {
            label: "Ship To Country/Area",
            name: "tcShipToCountryOrArea",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: initialValues.tcShipToCountryOrArea,
            events: {
              onChange: async (newValue: any, updatedValues: any) => {
                try {
                  await getState(newValue);
                  formRef.current.setFieldValue("tcShipToStateOrProvince", "");
                  Utills.cl("Updated form values:", updatedValues);
                } catch (error) {
                  console.error("Error loading states:", error);
                  notify("Failed to load states for selected country", "error");
                }
              },
            },
            props: {
              enableSearch: true,
              options: countries,
              placeholder: "Select the Country/Area",
              fullWidth: true,
            },
          },
          {
            label: "Ship To State/Province",
            name: "tcShipToStateOrProvince",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: initialValues.tcShipToStateOrProvince,
            events: {
              onChange: (newValue: any, updatedValues: any) => {
                Utills.cl("Field changed:", newValue);
              },
            },
            props: {
              enableSearch: true,
              options: states,
              placeholder: "Select the State/Province",
              fullWidth: true,
            },
          },
        ],
      },
    ],
    handleSubmit: async (values: any) => {
      try {
        if (getCopy) {
          await handleSaveData([values], null);
        } else if (selectedShipmentId && values.shipmentId && !getCopy) {
          const existingShipment = tableData.find(
            (item) => item.shipmentId === values.shipmentId
          );
          if (
            existingShipment &&
            existingShipment.tcShipmentNo !== values.tcShipmentNo
          ) {
            const row = {
              original: {
                tcShipmentNo: existingShipment.tcShipmentNo,
                shipmentId: values.shipmentId,
              },
            };
            setPendingSave(values);
            setOriginalValue(existingShipment.tcShipmentNo);
            await handleEditDelete(row);
          } else {
            await handleSaveData([values], values.shipmentId);
          }
        } else {
          await handleSaveData([values], null);
        }
      } catch (error) {
        console.error("Error in form submission:", error);
        notify("Error submitting form", "error");
      }
    },
  };

  // DATA TABLE SECTION

  // Table Columns Configuration
  const columns: MRT_ColumnDef<IShipmentDetail>[] = [
    {
      accessorKey: "tcShipmentDocNo",
      header: "Ship Doc Number",
      minSize: 100,
      maxSize: 200,
    },
    {
      accessorKey: "tcShipmentDate",
      header: "Shipment Date",
      enableHiding: false,
      minSize: 100,
      maxSize: 200,
    },
    {
      accessorKey: "tcShipToTeId",
      header: "Ship To TE-ID",
      minSize: 100,
      maxSize: 200,
    },
    {
      accessorKey: "tcShipToName",
      header: "Ship To Name",
      minSize: 100,
      maxSize: 200,
    },
    {
      accessorKey: "tcShipToCountryOrAreaDescription",
      header: "Ship To Country/Area",
      minSize: 100,
      maxSize: 200,
    },
    {
      accessorKey: "tcShipToStateOrProvinceDescription",
      header: "Ship To State/Province",
      minSize: 100,
      maxSize: 200,
    },
    {
      accessorKey: "tcShipToPostcode",
      header: "Ship To Postcode",
      minSize: 100,
      maxSize: 200,
    },
    {
      accessorKey: "tcShipToTown",
      header: "Ship To Town",
      minSize: 100,
      maxSize: 200,
    },
    {
      accessorKey: "tcShipmentGrossShippingWeight",
      header: "Gross Shipment Weight",
      minSize: 100,
      maxSize: 200,
      Cell: ({ cell }: { cell: MRT_Cell<IShipmentDetail, unknown> }) => {
        const value = cell.getValue() as string | undefined;
        return Utills.usNumberFormat({
          cell: { getValue: () => value ?? "0" },
        });
      },
    },
    {
      accessorKey: "tcShipmentInvoiceReferences",
      header: "Invoice References",
      minSize: 100,
      maxSize: 200,
      Cell: ({ cell }: { cell: MRT_Cell<IShipmentDetail, unknown> }) => {
        const invoiceRefArray = cell.getValue() as string[] | undefined;
        return Array.isArray(invoiceRefArray)
          ? invoiceRefArray.join(", ")
          : invoiceRefArray || "";
      },
    },
  ];

  // Table Action Handlers
  const handleEdit = (row: any) => {
    setCopy(false);
    const value = row?.original as IShipmentDetail;
    setSelectedShipmentId(value.shipmentId);

    if (TE_ID && value.shipmentId) {
      fetchShipmentDetailsV4(TE_ID, value.shipmentId)
        .then((shipmentsData) => {
          // Updated access here
          const data = shipmentsData?.response?.find((shipment:any)=>value?.tcShipmentDocNo===shipment?.tcShipmentInformation[0]?.tcShipmentDocNo) 
          if (
            data?.tcShipmentInformation &&
            data?.tcShipmentInformation.length > 0
          ) {
            const detail = data?.tcShipmentInformation[0]; // Also update this line
            const initialData: IShipmentDetail = {
              shipmentId: value.shipmentId,
              tcShipmentNo: detail.tcShipmentDocNo || "", // Assuming tcShipmentNo is derived from tcShipmentDocNo; adjust if needed
              tcShipmentDate: detail.tcShipmentDate || "",
              tcShipmentDocNo: detail.tcShipmentDocNo || "",
              tcShipmentGrossShippingWeight:
                detail.tcShipmentGrossShippingWeight?.toString() || "0",
              tcShipmentInvoiceReferences:
                detail.tcShipmentInvoiceReferences || [],
              tcShipToTeId: detail.tcShipToTeId || "",
              tcShipToName: detail.tcShipToName || "",
              tcShipToStreetAddress: detail.tcShipToStreetAddress || "",
              tcShipToPostcode: detail.tcShipToPostcode || "",
              tcShipToTown: detail.tcShipToTown || "",
              tcShipToCountryOrArea: detail.tcShipToCountryOrArea || "",
              tcShipToStateOrProvince: detail.tcShipToStateOrProvince || "",
              tcShipToCountryOrAreaDescription:
                detail.tcShipToCountryOrAreaDescription || "",
              tcShipToStateOrProvinceDescription:
                detail.tcShipToStateOrProvinceDescription || "",
              tcShipToArrangedByBuyer: detail?.tcShipToArrangedByBuyer || false,
            };
            setInitialValues(initialData);
            setDisable({
              disableShipToAddress1: !!detail.tcShipToStreetAddress,
              disableShipToTown: !!detail.tcShipToTown,
              disableShipToPostCode: !!detail.tcShipToPostcode,
              disableShipToCountryOrArea: !!detail.tcShipToCountryOrArea,
              disableShipToStateOrProvince: !!detail.tcShipToStateOrProvince,
            });
            toggleDrawer();
          } else {
            console.log("No consignee details found:", data);
            notify("No shipment details available for editing", "warning");
          }
        })
        .catch((err) => {
          console.error("Error fetching shipment details:", err);
          notify("Failed to fetch shipment details for editing", "error");
        });
    } else {
      notify("Invalid shipment data for editing", "error");
    }
  };

  const handleDelete = (row: any) => {
    const tcShipmentNo = row?.original?.tcShipmentDocNo;
    const shipmentId = row?.original?.shipmentId;
    setTcShipmentNo(tcShipmentNo);
    setSelectedShipmentId(shipmentId);
    setDialogOpen(true);
    setPopUpBtnName("Delete");
    setPopUpTitle("Confirm Delete");
    setPopUpMessage(
      `Deleting Shipment ${tcShipmentNo} will remove all the Associated Records, are you sure want to delete it?`
    );
  };

  const handleCopy = (row: any) => {
    // Check shipment limit before proceeding with copy
    if (tableData.length >= MAX_SHIPMENTS_ALLOWED) {
      notify(
        "Maximum limit of 50 shipments reached. Please remove some shipments before adding new ones.",
        "error"
      );
      return;
    }

    setCopy(true);
    const value = row?.original as IShipmentDetail;
    setSelectedShipmentId(null);

    if (TE_ID && value.shipmentId) {
      fetchShipmentDetailsV4(TE_ID, value.shipmentId)
        .then((data) => {
          const matchingShipment = data.response.find((item: any) => item.shipmentId === value.shipmentId);
          if (matchingShipment?.tcShipmentInformation && matchingShipment.tcShipmentInformation.length > 0) {
            const detail = matchingShipment.tcShipmentInformation[0];
            const initialData: IShipmentDetail = {
              shipmentId: null,
              tcShipmentNo: detail.tcShipmentDocNo || "",
              tcShipmentDate: detail.tcShipmentDate || "",
              tcShipmentDocNo: detail.tcShipmentDocNo || "",
              tcShipmentGrossShippingWeight:
                detail.tcShipmentGrossShippingWeight?.toString() || "0",
              tcShipmentInvoiceReferences:
                detail.tcShipmentInvoiceReferences || [],
              tcShipToTeId: detail.tcShipToTeId || "",
              tcShipToName: detail.tcShipToName || "",
              tcShipToStreetAddress: detail.tcShipToStreetAddress || "",
              tcShipToPostcode: detail.tcShipToPostcode || "",
              tcShipToTown: detail.tcShipToTown || "",
              tcShipToStateOrProvince: detail.tcShipToStateOrProvince || "",
              tcShipToCountryOrArea: detail.tcShipToCountryOrArea || "",
              tcShipToCountryOrAreaDescription:
                detail.tcShipToCountryOrAreaDescription || "",
              tcShipToStateOrProvinceDescription:
                detail.tcShipToStateOrProvinceDescription || "",
              tcShipToArrangedByBuyer: detail?.tcShipToArrangedByBuyer || false,
            };

            setDisable({
              disableShipToAddress1: !!detail.tcShipToStreetAddress,
              disableShipToTown: !!detail.tcShipToTown,
              disableShipToPostCode: !!detail.tcShipToPostcode,
              disableShipToCountryOrArea: !!detail.tcShipToCountryOrArea,
              disableShipToStateOrProvince: !!detail.tcShipToStateOrProvince,
            });

            setInitialValues(initialData);
            toggleDrawer();
          } else {
            console.log("No consignee details found:", data);
            notify("No shipment details available for copying", "warning");
          }
        })
        .catch((err) => {
          console.error("Error fetching shipment details:", err);
          notify("Failed to fetch shipment details for copying", "error");
        });
    } else {
      notify("Invalid shipment data for copying", "error");
    }
  };

  const handleEditDelete = (row: any) => {
    const tcShipmentNo = row?.original?.tcShipmentNo;
    const shipmentId = row?.original?.shipmentId;
    setTcShipmentNo(tcShipmentNo);
    setSelectedShipmentId(shipmentId);
    setDialogOpen(true);
    setPopUpBtnName("Save");
    setPopUpTitle("Confirm Save");
    setPopUpMessage(
      `Editing ShipmentNo ${tcShipmentNo} will remove all the Associated Records, are you sure want to edit it?`
    );
  };

  // API INTEGRATION SECTION

  // Data Loading Functions
  const loadFormData = async () => {
    try {
      if (!TE_ID) {
        notify("TE_ID is required to load shipments", "error");
        return;
      }

      setLoadingState(true);
      console.log("Fetching shipments for TE_ID:", TE_ID);

      const offset = pagination.pageIndex * pagination.pageSize;
      const response = await executeGet(TCAppConfig.GET_SHIPMENTS_ENDPOINT_V4, {
        tcId: TE_ID,
        limit: pagination.pageSize,
        offset: offset,
      });

      console.log("Shipments API Response:", response);

      const minDateFromApi = response?.data?.shipment_min_date;
      const normalizedMinDate =
        typeof minDateFromApi === "string" && minDateFromApi.trim()
          ? minDateFromApi.trim()
          : null;
      setShipmentMinDate(
        minDateFromApi === null ? null : normalizedMinDate
      );

      const result = response?.data?.response;
      if (!result || !Array.isArray(result)) {
        console.warn("No shipment data received or invalid format");
        setTableData([]);
        replaceTableDatas([]);
        setNextEnabled(true);
        setLoadingState(false);
        return;
      }

      const mappedData: IShipmentDetail[] = result
        .filter((item) => item?.tcShipmentInformation?.length > 0)
        .map((item: any) => {
          // Get the first consignee detail from the array
          const consigneeDetail = item.tcShipmentInformation[0];
          console.log("Processing shipment:", item.shipmentId, consigneeDetail);

          return {
            shipmentId: item.shipmentId,
            tcShipmentNo: consigneeDetail?.tcShipmentDocNo || "",
            tcShipmentDate: consigneeDetail?.tcShipmentDate || "",
            tcShipmentDocNo: consigneeDetail?.tcShipmentDocNo || "",
            tcShipmentGrossShippingWeight:
              consigneeDetail?.tcShipmentGrossShippingWeight?.toString() || "0",
            tcShipmentInvoiceReferences:
              consigneeDetail?.tcShipmentInvoiceReferences || [],
            tcShipToTeId: consigneeDetail?.tcShipToTeId || "",
            tcShipToStreetAddress: consigneeDetail?.tcShipToStreetAddress || "",
            tcShipToTown: consigneeDetail?.tcShipToTown || "",
            tcShipToPostcode: consigneeDetail?.tcShipToPostcode || "",
            tcShipToCountryOrArea: consigneeDetail?.tcShipToCountryOrArea || "",
            tcShipToStateOrProvince:
              consigneeDetail?.tcShipToStateOrProvince || "",
            tcShipToName: consigneeDetail?.tcShipToName || "",
            tcShipToArrangedByBuyer:
              consigneeDetail?.tcShipToArrangedByBuyer || false,
            tcShipToCountryOrAreaDescription:
              consigneeDetail?.tcShipToCountryOrAreaDescription ||
              countries.find(
                (c) => c.value === consigneeDetail?.tcShipToCountryOrArea
              )?.label ||
              "",
            tcShipToStateOrProvinceDescription:
              consigneeDetail?.tcShipToStateOrProvinceDescription ||
              states.find(
                (s) => s.value === consigneeDetail?.tcShipToStateOrProvince
              )?.label ||
              "",
          };
        });

      console.log("Mapped Shipment Data:", mappedData);

      setTableData(mappedData);
      setAPIData(mappedData);
      setRowCount(response?.data?.no_of_records || 0);

      // Update the form data storage
      if (getCurrentTableData().length > 0) {
        replaceTableDatas(mappedData);
      } else {
        updateTableData(mappedData);
      }

      setNextEnabled(true);
      setAPIShipmentsData(mappedData);
      setDeleteSave(false);
    } catch (err) {
      console.error("Failed to fetch TC Base Details:", err);
      notify("Failed to fetch shipments", "error");
    } finally {
      setLoadingState(false);
    }
  };

  const fetchTEDetallsFromAPI = async (
    value: string,
    offset: number,
    type: string,
    name?: string
  ): Promise<TEDetail[]> => {
    try {
      const queryParams: Record<string, string | number> = {
        query_param: type === "query" ? value : "",
        offset: offset,
        name: type === "name" ? value : "",
      };
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );
      return response.data || [];
    } catch (error) {
      console.error("Error fetching TE details:", error);
      notify("Error fetching TE details", "error");
      return [];
    }
  };

  const fetchShipmentDetailsV4 = async (tcId: string, shipmentId: string) => {
    try {
      const response = await executeGet(
        TCAppConfig.GET_SHIPMENTS_ENDPOINT_V4,
        {
          tcId,
          shipmentId,
        }
      );
      return response?.data;
    } catch (error) {
      console.error("Error fetching shipment details:", error);
      notify("Failed to fetch shipment details", "error");
      throw error;
    }
  };

  const getCountry = async () => {
    try {
      const result: any = await dtrackItService.getCountry();
      // Debug: getCountry result removed
      const countriesData = result.data.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      // Debug: countries data removed
      setCountries(countriesData);
      setCountryCodes(countriesData);
    } catch (err) {
      console.error(`Error in Fetching Countries: ${err}`);
      notify("Failed to load countries", "error");
    }
  };

  const getState = async (shipToCountryOrArea: string) => {
    try {
      if (!shipToCountryOrArea) {
        return;
      }
      // clear previous states while loading to avoid showing stale/country data
      setState([]);
      setStates([]);
      const normalizeCountryCode = (val?: string) => {
        if (!val) return "";
        const v = String(val).trim();
        if (v.startsWith("[")) return v;
        const m = v.match(/\(([A-Za-z0-9_-]+)\)$/);
        if (m) return m[1];
        return v;
      };

      const code = normalizeCountryCode(shipToCountryOrArea);
      if (!code) return;
      const result: any = await dtrackItService.getStateBasedonCountry(code);
      const list = Array.isArray(result?.data) ? result.data : [];
      const statesData = list.map((t: any) => ({
        value: t.code_1,
        label: `${t.description_1} (${t.code_1})`,
      }));
      setState(statesData);
      setStates(statesData);
    } catch (err) {
      console.error(`Error in Fetching States: ${err}`);
      notify("Failed to load states", "error");
    }
  };

  // Data Mapping and Saving Functions
  const showDateWarning = useCallback(
    (differenceInDays: number, minDate: string) => {
      return new Promise<boolean>((resolve) => {
        const roundedDifference = Math.round(differenceInDays);
        dateWarningResolverRef.current = resolve;
        setDateWarningDialog({
          open: true,
          title: "Shipment Date Warning",
          message:
            roundedDifference > 0
              ? `The difference between the Max and Min Shipment Date exceeds 90 days (${roundedDifference} days difference from earliest shipment date ${minDate}). Do you want to proceed?`
              : `The difference between the Max and Min Shipment Date exceeds 90 days from earliest shipment date ${minDate}. Do you want to proceed?`,
        });
      });
    },
    [setDateWarningDialog]
  );

  const closeDateWarningDialog = (decision: boolean) => {
    setDateWarningDialog({
      open: false,
      title: "",
      message: "",
    });
    if (dateWarningResolverRef.current) {
      dateWarningResolverRef.current(decision);
      dateWarningResolverRef.current = null;
    }
  };

  const handleDateWarningConfirm = () => closeDateWarningDialog(true);
  const handleDateWarningCancel = () => closeDateWarningDialog(false);

  const mapDataToRequest = (
    values: IShipmentDetail[],
    tcId: string
  ): IShipmentsRequest => {
    return {
      tcId,
      tcGrossShippingWeight: values.reduce(
        (sum, item) => sum + Number(item.tcShipmentGrossShippingWeight || 0),
        0
      ),
      tcShipmentInformation: values.map((item) => ({
        shipmentId: item.shipmentId || undefined,
        tcShipmentDate: item.tcShipmentDate,
        tcShipmentDocNo: item.tcShipmentDocNo,
        tcShipmentGrossShippingWeight: Number(
          item.tcShipmentGrossShippingWeight || 0
        ),
        tcShipmentInvoiceReferences: item.tcShipmentInvoiceReferences,
        tcShipToName: normalizeNaValue(item.tcShipToName),
        tcShipToTeId: item.tcShipToTeId,
        tcShipToStreetAddress: item.tcShipToStreetAddress,
        tcShipToPostcode: item.tcShipToPostcode,
        tcShipToTown: item.tcShipToTown,
        tcShipToStateOrProvince: item.tcShipToStateOrProvince,
        tcShipToCountryOrArea: item.tcShipToCountryOrArea,
        tcShipToArrangedByBuyer: item.tcShipToArrangedByBuyer,
        tcShipToCountryOrAreaDescription:
          item.tcShipToCountryOrAreaDescription ||
          countries.find((c) => c.value === item.tcShipToCountryOrArea)
            ?.label ||
          "",
        tcShipToStateOrProvinceDescription:
          item.tcShipToStateOrProvinceDescription ||
          states.find((s) => s.value === item.tcShipToStateOrProvince)?.label ||
          "",
      })),
    };
  };

  const handleSaveData = async (
    values: IShipmentDetail[],
    shipmentId: string | null
  ): Promise<boolean> => {
    try {
      const minDateReference = shipmentMinDate?.trim();
      let maxDateDifference = 0;

      if (minDateReference) {
        values.forEach((item) => {
          const normalizedDocNo = (item.tcShipmentDocNo || "")
            .trim()
            .toLowerCase();
          if (normalizedDocNo === "dropship") {
            return;
          }
          if (!item.tcShipmentDate) {
            return;
          }
          const differenceInDays = Math.abs(
            Utills.calculateDateDifferenceInDays(
              minDateReference,
              item.tcShipmentDate
            )
          );
          if (Number.isFinite(differenceInDays)) {
            maxDateDifference = Math.max(maxDateDifference, differenceInDays);
          }
        });
      }

      if (maxDateDifference > 90 && minDateReference) {
        const shouldProceed = await showDateWarning(
          maxDateDifference,
          minDateReference
        );
        if (!shouldProceed) {
          return false;
        }
      }

      const payload = mapDataToRequest(values, TE_ID || "");
      const isNewlyCreated = hasCreateTc && TE_ID ? "Yes" : "No";
      const finalUrl = `${TCAppConfig.POST_SHIPMENTS_ENDPOINT}?${
        TE_ID ? `tcId=${TE_ID}&` : ""
      }${shipmentId ? `shipmentId=${shipmentId}&` : ""}isNewlyCreated=${isNewlyCreated}`;
      const result = await executePost(finalUrl, payload);

      if (result?.status === 200) {
        notify("Saved Successfully", "success");
        toggleDrawer();
        resetForm();
        await onNextTab();
        await loadFormData();
        return true;
      } else {
        notify("Failed to save shipment", "error");
        return false;
      }
    } catch (error: unknown) {
      console.error("Error saving shipment:", error);
      notify("Error saving shipment", "error");
      return false;
    }
  };
  const handleConfirmSave = async () => {
    const isProceedAction = popUpBtnName === "Proceed";
    setDialogOpen(false);

    if (isProceedAction) {
      navigate(`/v4/tc/${formtype}/product-details/${TE_ID}`);
      return;
    }
    if (!selectedShipmentId) {
      notify("No shipment selected for deletion", "error");
      return;
    }

    let isSaveSuccess = false;
    try {
      if (pendingSave) {
        isSaveSuccess = await handleSaveData(
          [pendingSave],
          pendingSave.shipmentId
        );
      }

      if (!pendingSave || isSaveSuccess) {
        const res = await executeDelete(`${TCAppConfig.TC_DELETE_API_V4}`, {
          tcId: TE_ID,
          shipmentId: selectedShipmentId,
          shipmentNo: tcShipmentNo,
        });

        if (res?.status === 200) {
          if (pendingSave) {
            notify("Previous Shipment deleted successfully", "success");
          } else {
            notify("Shipment deleted successfully", "success");
          }
          await loadFormData();
        } else {
          notify("Failed to delete shipment", "error");
        }
      }
    } catch (error) {
      console.error("Error deleting shipment:", error);
      notify("Failed to delete shipment", "error");
    } finally {
      setDialogOpen(false);
      setPendingSave(null);
      setSelectedShipmentId(null);
    }
  };

  const handleCancelSave = () => {
    setDialogOpen(false);
    if (drawerOpen) {
      toggleDrawer();
    }
    setCopy(false);
    setPendingSave(null);
    setSelectedShipmentId(null);
  };

  // INITIALIZATION AND LIFECYCLE

  const setZustandInitialState = (initialState?: any) => {
    const defaultState = {
      tcId: "",
      tcGrossShippingWeight: "",
      tcShipmentInformation: [
        {
          tcShipmentConsigneeOrder: "",
          tcShipmentNo: "",
          tcShipmentDate: "",
          tcShipmentDocNo: "",
          tcGrossShippingWeight: "",
          tcShipmentInvoiceReferences: [],
          tcShipToName: "",
          tcShipToTeId: "",
          tcShipToStreetAddress: "",
          tcShipToPostcode: "",
          tcShipToTown: "",
          tcShipToStateOrProvince: "",
          tcShipToCountryOrArea: "",
          isShipToTeCertified: "Y",
        },
      ],
    };

    console.log(`initialState from shipments ${initialState}`);
    Object.entries(initialState ?? defaultState).forEach(([key, value]) => {
      updateField(key, value);
    });
  };

  // Effect Hooks
  useEffect(() => {
    const initializeComponent = async () => {
      try {
        resetFormValueByPage();
        setZustandInitialState(null);

        if (TE_ID) {
          await loadFormData();
        } else {
          updateTableData([]);
        }
      } catch (error) {
        console.error("Error initializing component:", error);
        notify("Error initializing component", "error");
      }
    };

    initializeComponent();
  }, [TE_ID, pagination?.pageIndex]);

  useEffect(() => {
    const initializeFormData = async () => {
      try {
        const shipmentsTableDatas = getCurrentTableData();
        // Debug: shipmentsTableDatas removed

        if (shipmentsTableDatas) {
          setData(shipmentsTableDatas);
        }

        await getCountry();
      } catch (error) {
        console.error("Error initializing form data:", error);
        notify("Error loading initial form data", "error");
      }
    };

    initializeFormData();
  }, []);

  useEffect(() => {
    if (initialValues.tcShipToCountryOrArea) {
      getState(initialValues.tcShipToCountryOrArea);
    }
  }, [initialValues.tcShipToCountryOrArea]);

  // NAVIGATION HANDLERS

  // In onNext function
  const onNext = async () => {
    try {
      if (!getTCStandardGOTS && tableData.length === 0) {
        notify("Please add at least one shipment", "error");
        return;
      }
      if (getTCStandardGOTS && tableData.length === 0) {
        const gotsPayload: IShipmentDetail = {
          shipmentId: null,
          tcShipmentNo: "",
          tcShipmentDate: "",
          tcShipmentDocNo: "",
          tcShipmentGrossShippingWeight: "0",
          tcShipmentInvoiceReferences: [],
          tcShipToName: "",
          tcShipToTeId: "",
          tcShipToStreetAddress: "",
          tcShipToPostcode: "",
          tcShipToTown: "",
          tcShipToStateOrProvince: "",
          tcShipToCountryOrArea: "",
          tcShipToCountryOrAreaDescription: "",
          tcShipToStateOrProvinceDescription: "",
          tcShipToArrangedByBuyer: false,
        };
        await handleSaveData([gotsPayload], null);
      }
      
      /******* */

      const shipmentDates:Date[] = [];

      tableData.forEach(shipmentInfo => {
          if (shipmentInfo.tcShipmentDocNo !== "dropship" && shipmentInfo.tcShipmentDate) {
            const date = new Date(shipmentInfo.tcShipmentDate);
            if (!isNaN(date.getTime())) {
              shipmentDates.push(date);
            }
          }
      });

      // Step 2: Check date range
      if (shipmentDates.length > 0) {
        const minDate = new Date(Math.min(...shipmentDates?.map(d=>d.getTime())));
        const maxDate = new Date(Math.max(...shipmentDates?.map(d=>d.getTime())));
        const diffInDays = (maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24);

        if (diffInDays > 90) {
          setDialogOpen(true);
          setPopUpTitle("Shipping date Out of range");
          setPopUpMessage("Shipping date range is greater than 90 days. Are you sure you want to continue?");
          setPopUpBtnName("Proceed");
          return;
        } else {
          console.log("✅ Shipping date range is within 90 days.");
        }
      }

      /***** */
      navigate(`/v4/tc/${formtype}/product-details/${TE_ID}`);
    } catch (error) {
      console.error("Error navigating to next step:", error);
      notify("Error navigating to next step", "error");
    }
  };

  const onBack = () => {
    navigate(`/v4/tc/${formtype}/buyer-seller-products/${TE_ID}`);
  };

  // Calculate total gross shipping weight
  const totalGrossShippingWeight = useMemo(() => {
    return tableData.reduce(
      (sum: number, item: IShipmentDetail) =>
        sum + parseFloat(item.tcShipmentGrossShippingWeight || "0"),
      0
    );
  }, [tableData]);

  // RENDER SECTION
  return (
    <>
      {loading ? (
        <LoaderComponent />
      ) : (
        <>
          {/* Gross Shipping Weight Display */}
          <Typography
            variant="body1"
            sx={{ fontWeight: "bold", fontSize: "1.25rem" }}
          >
            Shipments Information
          </Typography>
          <Box
            display="flex"
            mb={3}
            justifyContent="space-between"
            width="100%"
          >
            <Typography variant="body1">
              Gross Shipping Weight: {totalGrossShippingWeight} kg
            </Typography>
          </Box>

          {/* Add Shipment Button */}
          <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
            {!hasSubmitTc && (
              <Tooltip
                title={
                  rowCount >= MAX_SHIPMENTS_ALLOWED
                    ? "Maximum limit of 50 shipments reached. Please remove some shipments before adding new ones."
                    : ""
                }
              >
                <span>
                  <StyledButton
                    variant="contained"
                    sx={{ boxShadow: "none" }}
                    onClick={toggleDrawer}
                    // disabled={rowCount >= MAX_SHIPMENTS_ALLOWED}
                  >
                    Add Shipment Information
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>

          {/* Form Modal */}
          <ModalPopup
            open={drawerOpen}
            onClose={toggleDrawer}
            onSave={handleSubmit}
          >
            <FormBuilder
              formConfig={formConfig}
              onSubmit={formConfig.handleSubmit}
              formRef={formRef}
            />
          </ModalPopup>

          {/* Data Table */}
          <Box display="grid">
            <CustomTable
              columns={columns}
              data={tableData}
              enableTopToolbar={false}
              enableBottomToolbar={true}
              enableRowActions={!hasSubmitTc}
              rowCount={rowCount}
              paginations={pagination}
              setPaginations={setPagination}
              // renderDetailPanel={({ row }) =>
              //   row.getIsExpanded() ? (
              //     <RenderDetailPanel row={row} />
              //   ) : (
              //     <Typography>No data available</Typography>
              //   )
              // }
              renderRowActions={({ row }) => (
                <ActionsMenu
                  onEdit={() => handleEdit(row)}
                  onDelete={() => handleDelete(row)}
                  onCopy={() => handleCopy(row)}
                  row={row}
                />
              )}
            />
          </Box>

          {/* Navigation Buttons */}
          {!drawerOpen && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 2,
                gap: "0.5rem",
              }}
            >
              {hasSubmitTc ? (
                <>
                  <InnerButton onClick={onBack}>Back</InnerButton>
                  <InnerButton onClick={onNext}>Next</InnerButton>
                </>
              ) : (
                <>
                  <NavLink to="/v4/tc-dashboard">
                    <InnerButton>Home</InnerButton>
                  </NavLink>
                  {TE_ID && <InnerButton onClick={onBack}>Back</InnerButton>}
                  {tableData.length > 0 &&
                    tableData.length <= MAX_SHIPMENTS_ALLOWED && (
                      <InnerButton
                        onClick={onNext}
                        disabled={tableData.length > MAX_SHIPMENTS_ALLOWED}
                      >
                        {hasSubmitTc ? "Next" : "Next"}
                      </InnerButton>
                    )}
                </>
              )}
            </Box>
          )}

          {/* Confirmation Dialog */}
          <AlertPopup
            open={dialogOpen}
            title={popUpTitle}
            message={popUpMessage}
            onClose={handleCancelSave}
            onConfirm={handleConfirmSave}
            confirmText={popUpBtnName}
            cancelText="Cancel"
          />
          <AlertPopup
            open={dateWarningDialog.open}
            title={dateWarningDialog.title}
            message={dateWarningDialog.message}
            onClose={handleDateWarningCancel}
            onConfirm={handleDateWarningConfirm}
            confirmText="Yes"
            cancelText="No"
          />
        </>
      )}
    </>
  );
};

export default Shipments;
